import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

const TZ = 'Europe/Copenhagen';

const formatTime = (iso, lang) => {
    try {
        return new Date(iso).toLocaleTimeString(
            lang === 'da' ? 'da-DK' : 'en-GB',
            { hour: '2-digit', minute: '2-digit', timeZone: TZ }
        );
    } catch {
        return '';
    }
};

/**
 * Schedule-derived "is security open yet?" note. Renders only in the pre-dawn
 * window on the homepage (when `securityOpening.shouldShow` is true), or always
 * when `forceShow` is set (e.g. the opening-hours landing page).
 */
function SecurityOpeningNote({ securityOpening, forceShow = false }) {
    const { t, lang } = useTranslation('opening-hours');
    const [mounted, setMounted] = useState(false);
    const tracked = useRef(false);

    useEffect(() => {
        // Defer time-formatting to the client to avoid a hydration mismatch.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMounted(true);
        // Track once per visit, not once per poll: the homepage refetches every
        // 60s and hands us a new securityOpening object each cycle, so keying
        // the event on that object re-fired it every minute and overcounted by
        // dwell time. The ref guard makes it one event per pre-dawn viewer.
        if (
            !tracked.current &&
            securityOpening?.shouldShow &&
            typeof umami !== 'undefined'
        ) {
            tracked.current = true;
            umami.track('security-opening-note-shown', {
                isOpenNow: securityOpening.isOpenNow,
                locale: lang
            });
        }
    }, [securityOpening, lang]);

    if (!securityOpening) return null;
    if (!forceShow && !securityOpening.shouldShow) return null;
    // Times are formatted in Copenhagen tz on the client; skip SSR to avoid
    // a hydration mismatch (mirrors FormattedDate).
    if (!mounted) return null;

    const {
        isOpenNow,
        opensAt,
        firstDeparture,
        firstFlightLabel,
        drivenByFirstFlight
    } = securityOpening;

    const opensTime = formatTime(opensAt, lang);
    const firstTime = firstDeparture ? formatTime(firstDeparture, lang) : null;

    const firstFlightLine = firstTime
        ? firstFlightLabel
            ? t('first_flight', { flight: firstFlightLabel, time: firstTime })
            : t('first_departure_time', { time: firstTime })
        : null;

    return (
        <div className="mx-auto my-5 max-w-md rounded-lg bg-white p-4 text-left text-gray-800 shadow-lg">
            {isOpenNow ? (
                <>
                    <p className="text-lg font-bold">
                        ✅ {t('card_open_title')}
                    </p>
                    <p className="mt-1">
                        {t('opened_at', { time: opensTime })}
                    </p>
                    {firstFlightLine && (
                        <p className="mt-1 text-sm text-gray-600">
                            {firstFlightLine}
                        </p>
                    )}
                </>
            ) : (
                <>
                    <p className="text-lg font-bold">
                        🔒 {t('card_closed_title')}
                    </p>
                    <p className="mt-1">
                        {drivenByFirstFlight
                            ? t('opens_around', { time: opensTime })
                            : t('opens_at_default', { time: opensTime })}
                    </p>
                    {firstFlightLine && (
                        <p className="mt-1 text-sm text-gray-600">
                            {firstFlightLine}
                        </p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                        {t('rule_short')}
                    </p>
                </>
            )}
            {!forceShow && (
                <Link
                    href="/security-opening-hours"
                    className="mt-2 inline-block text-sm text-[var(--secondary)] underline"
                >
                    {t('learn_more')}
                </Link>
            )}
        </div>
    );
}

export default SecurityOpeningNote;
